import classNames from 'classnames';
import type { Moment } from 'moment';
import moment from 'moment';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import Combobox from './Combobox';
import type { AmpmType, RCPanelProps } from './iRCTimePicker';

function generateOptions(length: number, disabledOptions: number[], hideDisabledOptions: boolean, step = 1) {
    const arr = [];
    for (let value = 0; value < length; value += +step) {
        if (!disabledOptions || disabledOptions.indexOf(value) < 0 || !hideDisabledOptions) {
            arr.push(value);
        }
    }
    return arr;
}

function toNearestValidTime(time: Moment, hourOptions: number[], minuteOptions: number[], secondOptions: number[]) {
    const hour = hourOptions.slice().sort((a, b) => Math.abs(time.hour() - a) - Math.abs(time.hour() - b))[0];
    const minute = minuteOptions.slice().sort((a, b) => Math.abs(time.minute() - a) - Math.abs(time.minute() - b))[0];
    const second = secondOptions.slice().sort((a, b) => Math.abs(time.second() - a) - Math.abs(time.second() - b))[0];
    return moment(`${hour}:${minute}:${second}`, 'HH:mm:ss');
}

const defaultProps = {
    prefixCls: 'rc-time-picker-panel',
    disabledHours: () => [],
    disabledMinutes: () => [],
    disabledSeconds: () => [],
    defaultOpenValue: moment(),
    use12Hours: false
};

const Panel = forwardRef<any, RCPanelProps>((props, ref) => {
    const mergedProps = { ...defaultProps, ...props };
    const {
        prefixCls,
        value: propsValue,
        onChange,
        onAmPmChange,
        use12Hours,
        disabledHours,
        defaultOpenValue,
        fieldid,
        className,
        disabledMinutes,
        disabledSeconds,
        hideDisabledOptions,
        showHour,
        showMinute,
        showSecond,
        format,
        renderExtraFooter,
        onClick,
        hourStep,
        minuteStep,
        secondStep,
        width,
        dir
    } = mergedProps;

    const [value, setValue] = useState<Moment | null | undefined>(propsValue);

    // React18改造 - 使用 useEffect 替代 componentDidUpdate
    useEffect(() => {
        if (propsValue && propsValue !== value) {
            setValue(propsValue);
        }
    }, [propsValue]);

    const handleChange = useCallback((newValue: Moment) => {
        setValue(newValue);
        onChange?.(newValue);
    }, [onChange]);

    const handleAmPmChange = useCallback((ampm: AmpmType) => {
        onAmPmChange?.(ampm);
    }, [onAmPmChange]);

    const isAM = useCallback(() => {
        const realValue = value || defaultOpenValue;
        return realValue.hour() >= 0 && realValue.hour() < 12;
    }, [value, defaultOpenValue]);

    const disabledHoursHandler = useCallback(() => {
        let disabledOptions = disabledHours();
        if (use12Hours && Array.isArray(disabledOptions)) {
            if (isAM()) {
                disabledOptions = disabledOptions.filter(h => h < 12).map(h => (h === 0 ? 12 : h));
            } else {
                disabledOptions = disabledOptions.map(h => (h === 12 ? 12 : h - 12));
            }
        }
        return disabledOptions;
    }, [use12Hours, disabledHours, isAM]);

    // 暴露方法给父组件（如果需要）
    useImperativeHandle(ref, () => ({}), []);

    const disabledHourOptions = useMemo(() => disabledHoursHandler(), [disabledHoursHandler]);
    const disabledMinuteOptions = useMemo(() =>
        disabledMinutes(value ? value.hour() : null),
    [disabledMinutes, value]
    );
    const disabledSecondOptions = useMemo(() =>
        disabledSeconds(value ? value.hour() : null, value ? value.minute() : null),
    [disabledSeconds, value]
    );
    const hourOptions = useMemo(() =>
        generateOptions(24, disabledHourOptions, hideDisabledOptions, hourStep),
    [disabledHourOptions, hideDisabledOptions, hourStep]
    );
    const minuteOptions = useMemo(() =>
        generateOptions(60, disabledMinuteOptions, hideDisabledOptions, minuteStep),
    [disabledMinuteOptions, hideDisabledOptions, minuteStep]
    );
    const secondOptions = useMemo(() =>
        generateOptions(60, disabledSecondOptions, hideDisabledOptions, secondStep),
    [disabledSecondOptions, hideDisabledOptions, secondStep]
    );

    const validDefaultOpenValue = useMemo(() =>
        toNearestValidTime(defaultOpenValue, hourOptions, minuteOptions, secondOptions),
    [defaultOpenValue, hourOptions, minuteOptions, secondOptions]
    );

    return (
        <div
            className={classNames({
                [`${prefixCls}-inner`]: true,
                [`${className}`]: !!className
            })}
            fieldid={fieldid ? `${fieldid}_inner` : undefined}
            onClick={onClick}
        >
            <Combobox
                prefixCls={prefixCls}
                value={value}
                defaultOpenValue={validDefaultOpenValue}
                format={format}
                onChange={handleChange}
                onAmPmChange={handleAmPmChange}
                showHour={showHour}
                showMinute={showMinute}
                showSecond={showSecond}
                hourOptions={hourOptions}
                minuteOptions={minuteOptions}
                secondOptions={secondOptions}
                disabledHours={disabledHoursHandler}
                disabledMinutes={disabledMinutes}
                disabledSeconds={disabledSeconds}
                use12Hours={use12Hours}
                isAM={isAM()}
                width={width}
                dir={dir}
            />
            {renderExtraFooter?.()}
        </div>
    );
});

Panel.displayName = 'Panel';

export default Panel;